import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, QrCode, Link2, Sparkles, Info } from "lucide-react";
import api, { apiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { CampaignProductList } from "@/components/CampaignProductList";

export default function Refer() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [campaigns, setCampaigns] = useState([]);
  const [cid, setCid] = useState("");
  const [alloc, setAlloc] = useState(null);
  const [selected, setSelected] = useState("");
  const [links, setLinks] = useState([]);
  const [qr, setQr] = useState(null);
  const [showSlabs, setShowSlabs] = useState(false);
  const [bonus, setBonus] = useState(null);

  const origin = window.location.origin;
  const selCampaign = campaigns.find((c) => c.id === cid);
  const allLinks = campaigns.filter((c) => c.campaign_code);
  const loadLinks = () => api.get("/referral/links").then((r) => setLinks(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/referral/campaigns").then((r) => { setCampaigns(r.data); if (r.data[0]) setCid(r.data[0].id); }).catch(() => {});
    api.get("/referral/bonuses").then((r) => setBonus(r.data)).catch(() => {});
    loadLinks();
  }, []);

  useEffect(() => {
    if (!cid) return;
    api.get(`/referral/allocation/${cid}`).then((r) => {
      setAlloc(r.data);
      setSelected(String(r.data.locked_allocation ?? r.data.max_payout ?? r.data.cap));
    }).catch(() => {});
  }, [cid]);

  const create = async () => {
    try {
      const { data } = await api.post("/referral/links", { campaign_id: cid, selected_payout: Number(selected) });
      toast.success(t("ref_link_created"));
      loadLinks();
      navigator.clipboard?.writeText(`${origin}/r/${data.link_id}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success(t("ref_copied")); };
  const whatsapp = (url, name) => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out ${name} on Credit Kendra: ${url}`)}`, "_blank");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">Refer &amp; Earn</h1>
        <p className="text-slate-500 mt-1">{t("ref_subtitle")}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <label className="text-sm font-semibold text-slate-600">Campaign</label>
        <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="refer-campaign-select"
          className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-3 bg-white font-medium">
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.payout_type === "percent" && c.payout_percent ? `${c.name.replace(" Campaign", "")} · ${c.payout_percent}% of amount` : `${c.name.replace(" Campaign", "")} · payout ${inr(c.payout_pool)}`}</option>)}
        </select>

        {alloc && (
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-400 uppercase">Payout per conversion</p>
                {selCampaign?.payout_slabs?.length > 0 && (
                  <button type="button" onClick={() => setShowSlabs(!showSlabs)} data-testid="slab-info-btn" className="text-slate-400 hover:text-teal-600" title="Slab details"><Info className="w-4 h-4" /></button>
                )}
              </div>
              <p className="font-mono-num font-bold text-xl text-[#0B223D] mt-1">{inr(alloc.cap)}</p>
              <p className="text-xs text-slate-400 mt-1">{t("ref_payout_note")}</p>
              {showSlabs && selCampaign?.payout_slabs?.length > 0 && (
                <div className="mt-2 bg-teal-50 border border-teal-100 rounded-lg p-2.5 space-y-1" data-testid="slab-info-panel">
                  <p className="text-[10px] font-bold text-teal-700 uppercase">{t("ref_slab_title")}</p>
                  <p className="text-xs text-slate-600">Base: <b className="font-mono-num">{inr(selCampaign.payout_pool)}</b> / conversion</p>
                  {[...selCampaign.payout_slabs].sort((a, b) => a.min_conversions - b.min_conversions).map((s, i) => (
                    <p key={i} className="text-xs text-slate-600">{s.min_conversions}+ leads → <b className="font-mono-num text-teal-700">{inr(s.payout)}</b> / conversion</p>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-400 uppercase">You keep per conversion</p>
              <Input type="number" value={selected} disabled={alloc.locked_allocation != null || user?.is_bulk} data-testid="refer-payout-input"
                min={alloc.min_payout} max={alloc.max_payout}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") { setSelected(""); return; }
                  const n = Math.min(Number(v), alloc.max_payout);
                  setSelected(String(n < 0 ? 0 : n));
                  if (Number(v) > alloc.max_payout) toast.error(t("ref_max_toast", { max: inr(alloc.max_payout) }));
                }} className="mt-1 h-10 font-mono-num font-bold" />
              <p className="text-xs text-slate-400 mt-1">{user?.is_bulk ? t("ref_bulk_rates") : t("ref_max_note", { max: inr(alloc.max_payout) })}</p>
            </div>
          </div>
        )}
        {alloc?.locked_allocation != null && (
          <p className="mt-3 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">Your allocation for this campaign is locked at {inr(alloc.locked_allocation)} and cannot be changed.</p>
        )}
        <Button onClick={create} data-testid="refer-create-btn" className="mt-5 bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-11 rounded-xl px-6">
          <Sparkles className="w-4 h-4 mr-1.5" /> Generate referral link
        </Button>
      </div>
      {/* professional product list — BankSathi style */}
      <div data-testid="campaign-product-list">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px flex-1 bg-slate-200" />
          <h3 className="font-display font-extrabold text-sm tracking-widest text-[#0B223D] uppercase">Products — Share &amp; Earn</h3>
          <div className="h-px flex-1 bg-slate-200" />
        </div>
        <CampaignProductList campaigns={allLinks} user={user} origin={origin} />
      </div>

      {/* invite link */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="font-display font-bold text-[#0B223D] flex items-center gap-2"><Link2 className="w-5 h-5 text-teal-500" /> Your invite link</h3>
        <p className="text-sm text-slate-500 mt-1">Anyone who registers via this link becomes your direct referral.</p>
        {bonus && bonus.amount > 0 && (
          <p className="text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 mt-3" data-testid="invite-bonus-note">
            {t("ref_invite_bonus", { amount: bonus.amount, min: bonus.min_withdrawal, used: bonus.month_used, cap: bonus.monthly_cap })}
          </p>
        )}
        <div className="flex gap-2 mt-3">
          <Input readOnly value={`${origin}/register?ref=${user?.user_code}`} className="h-11 font-mono-num text-sm" data-testid="refer-invite-link" />
          <Button variant="outline" onClick={() => copy(`${origin}/register?ref=${user?.user_code}`)} className="h-11" data-testid="invite-copy-btn"><Copy className="w-4 h-4" /></Button>
          <Button variant="outline" className="h-11" data-testid="invite-whatsapp-btn"
            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(t("ref_invite_wa", { amount: bonus?.amount ?? 100, link: `${origin}/register?ref=${user?.user_code}`, min: bonus?.min_withdrawal ?? 1000 }))}`, "_blank")}><Share2 className="w-4 h-4 text-teal-600" /></Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2" data-testid="invite-tnc">{t("ref_tnc", { amount: bonus?.amount ?? 100, min: bonus?.min_withdrawal ?? 1000, cap: bonus?.monthly_cap ?? 10 })}</p>
      </div>

      {bonus && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="referral-bonus-tracker">
          <h3 className="font-display font-bold text-[#0B223D]">Referral bonus tracker</h3>
          <p className="text-sm text-slate-500 mt-1">{t("ref_tracker_sub", { amount: bonus.amount, min: bonus.min_withdrawal })}</p>
          <div className="space-y-2 mt-4">
            {bonus.bonuses.map((b) => (
              <div key={b.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3" data-testid={`bonus-${b.referred_code}`}>
                <div><p className="font-semibold text-sm text-[#0B223D]">{b.referred_name}</p><p className="text-xs font-mono-num text-slate-400">{b.referred_code}</p></div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${b.status === "credited" ? "bg-teal-50 text-teal-700" : b.status === "capped" ? "bg-slate-200 text-slate-500" : "bg-amber-50 text-amber-700"}`}>
                  {b.status === "credited" ? `+₹${b.amount} credited` : b.status === "capped" ? "Monthly cap over" : "Pending (withdrawal wait)"}
                </span>
              </div>
            ))}
            {!bonus.bonuses.length && <p className="text-sm text-slate-400">{t("ref_no_bonus")}</p>}
          </div>
        </div>
      )}

      {/* my links */}
      <div>
        <h3 className="font-display font-bold text-lg text-[#0B223D] mb-3">My referral links</h3>
        {links.length === 0 ? <p className="text-slate-400 text-sm">No links yet. Create one above.</p> : (
          <div className="space-y-3">
            {links.map((l) => {
              const url = `${origin}/r/${l.link_id}`;
              return (
                <div key={l.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`refer-link-${l.link_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#0B223D]">{l.campaign_name}</p>
                    <p className="text-xs text-slate-400 font-mono-num truncate">{url}</p>
                    <p className="text-xs text-slate-400 mt-0.5">You keep {inr(l.allocation)} · {l.clicks} clicks · {l.conversions} conversions</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copy(url)} data-testid={`copy-${l.link_id}`}><Copy className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => whatsapp(url, l.campaign_name)}><Share2 className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => setQr(qr === l.link_id ? null : l.link_id)}><QrCode className="w-4 h-4" /></Button>
                  </div>
                  {qr === l.link_id && (
                    <img alt="QR" className="w-28 h-28 rounded-lg border border-slate-200" src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(url)}`} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
